//for pagination at contacts page
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

interface ContactsPaginationProps {
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  onPageChange: (page: number) => void
  onLimitChange?: (limit: number) => void
  title?: string
}



/**
 * ContactsPagination component
 *
 * This component renders a pagination component for the contacts page.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.contacts - The array of contacts data to display.
 * @param {number} props.currentPage - The current page number.
 * @param {number} props.pageSize - The number of contacts per page.
 * @param {number} props.totalPages - The total number of pages.
 * @param {(pageNumber: number) => void} props.onPageChange - The callback function for page change.
 * @returns {JSX.Element} The rendered contacts pagination component.
 * @author uplSouvik
 */

export function ContactsPagination({ pagination, onPageChange, onLimitChange, title = "Contacts" }: ContactsPaginationProps) {
  if (!pagination?.page) return null

  const start = pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.limit) + 1
  const end = Math.min(pagination.page * pagination.limit, pagination.total)

  return (
    <div className="flex items-center justify-between mt-4">
      <div className="text-sm text-gray-600">
        Showing {start} to {end} of {pagination.total} {title}
      </div>
      <div className="flex items-center gap-2">
        {onLimitChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <Select
              value={pagination.limit.toString()}
              onValueChange={(value) => onLimitChange(Number(value))}
            >
              <SelectTrigger className="w-[80px] h-8">
                <SelectValue placeholder="10" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Separator for contextual actions */}
        <div className="hidden sm:block w-px h-6 bg-border mx-1"></div>
        <Button
          variant="outline"
          type="button"
          size="sm"
          disabled={pagination.page === 1}
          onClick={() => onPageChange(pagination.page - 1)}
        >
          Previous
        </Button>
        <span className="text-sm">
          Page {pagination.page} of {pagination.pages}
        </span>
        <Button
          variant="outline"
          type="button"
          size="sm"
          disabled={pagination.page === pagination.pages}
          onClick={() => onPageChange(pagination.page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}